/** 결제위젯을 **싣고 띄우는 유일한 자리**. 화면은 이 파일 너머를 모른다.
 *
 * `apps/api/src/lib/pg.ts` 와 같은 규율이다. PG 는 수수료율·정산 주기·심사 결과 때문에
 * 갈아끼우게 되고, 화면이 토스 전역 객체를 직접 만지면 그날 고칠 자리가 화면 수만큼 생긴다.
 *
 * ## ⭐ SDK 를 **번들에 넣지 않고 토스 도메인에서 싣는다** (2026-09-25 판단)
 *
 * 고른 것: 이 화면에 들어왔을 때만 `https://js.tosspayments.com/v2/standard` 를 `<script>`
 * 로 붙인다. npm 패키지(`@tosspayments/tosspayments-sdk`)를 물리지 않았다. 근거 셋이다.
 *
 * ① **결제창 코드는 우리가 고정하면 안 되는 코드다.** 카드사 규칙·보안 수정이 토스 쪽에서
 *    나가는데, 번들에 굳혀 두면 그 수정이 **우리 재배포를 기다린다.** 토스도 CDN 적재를
 *    전제로 배포한다.
 * ② **다른 화면이 이 무게를 지지 않는다.** 결제는 작업 하나에 한 번 지나는 자리인데
 *    번들에 넣으면 목록·검토·내보내기까지 그 바이트를 내려받는다.
 * ③ **갈아끼울 때 지울 것이 이 파일 하나다.** 의존성과 잠금 파일이 따라오지 않는다.
 *
 * ⛔ **치르는 값도 적어 둔다: CDN 이 안 열리면 결제가 안 된다.** 그래서 이 파일은 실패를
 * 삼키지 않고 `WidgetLoadError` 로 올리고, **화면이 그것을 또렷하게 말해야 한다.** 조용히
 * 빈 자리로 두면 셀러는 결제창이 뜨기를 기다린다.
 *
 * ## ⛔ 키를 이 파일에 적지 않는다
 *
 * `clientKey` 는 **서버가 `GET /api/payments/checkout/:jobId` 응답으로 준다.** 소스에 박으면
 * 테스트 키와 라이브 키를 바꾸는 일이 프런트엔드 재배포가 되고, 그 사이에 서버와 위젯이
 * 다른 키를 쓰는 상태가 생긴다. 그 상태의 증상은 승인 단계의 401 하나뿐이라 원인이 안 보인다.
 */

import { paymentClientErrorCode, type PaymentWidgetStage } from "@makit/shared";

/** SDK 주소. **테스트 키와 라이브 키가 같은 주소를 쓰고 키가 갈래를 정한다**(`lib/pg.ts` 의
 *  `TOSS_API_BASE` 와 같다). 심사 통과 뒤에 고칠 것은 서버 시크릿 하나이고 이 줄이 아니다. */
const SDK_SRC = "https://js.tosspayments.com/v2/standard";

/** SDK 를 기다리는 시간. ⚠️ **`onerror` 가 안 오는 실패가 있다**(연결이 열린 채 응답이 안
 *  오는 경우). 그때 이 값이 없으면 화면이 영원히 「불러오는 중」으로 선다. */
const SDK_TIMEOUT_MS = 15_000;

/** 위젯이 **어느 단계에서 · 어떤 코드로** 실패했는가. 화면이 셀러에게 `실패 코드:` 로 보이고 서버에
 *  보고하는 값이다(`POST /api/payments/client-error`. `docs/ERROR_LOGGING.md` §3-4-5).
 *
 * ⛔ **토스가 준 `message` 를 담지 않는다.** 셀러가 읽을 우리 문구가 아니고(아래 `WidgetReturn` 의
 * 같은 규약), 보고로 새면 Workers Logs 에서 지울 수 없다. `code` 는 `paymentClientErrorCode` 를 거친
 * 값만 담는다. 화면과 로그가 같은 낱말을 보이게. */
export class PaymentWidgetError extends Error {
  readonly stage: PaymentWidgetStage;
  readonly code: string;
  constructor(stage: PaymentWidgetStage, code: string, message = "결제위젯이 실패했다") {
    super(message);
    this.name = "PaymentWidgetError";
    this.stage = stage;
    this.code = paymentClientErrorCode(code);
  }
}

/** SDK 를 못 실었다. **키와 무관하다**(CDN · 연결). 코드는 토스가 준 것이 아니라 우리가 붙인 셋이다:
 *  `SDK_LOAD_FAILED`(스크립트 오류) · `SDK_TIMEOUT`(제때 안 옴) · `SDK_MISSING`(읽었는데 전역이 없음). */
export class WidgetLoadError extends PaymentWidgetError {
  constructor(code: "SDK_LOAD_FAILED" | "SDK_TIMEOUT" | "SDK_MISSING", message: string) {
    super("load", code, message);
    this.name = "WidgetLoadError";
  }
}

/** 토스 SDK 오류에서 `code` 만 뽑는다. SDK 오류는 `.code` 를 단다. 없거나 모양이 틀리면 `UNKNOWN`. */
export function tossErrorCode(err: unknown): string {
  const raw = typeof err === "object" && err !== null ? (err as { code?: unknown }).code : undefined;
  return paymentClientErrorCode(raw);
}

/** 한 단계를 돌리고, 실패하면 그 단계 이름을 단 오류로 바꿔 올린다. **이미 갈래가 붙은 오류는 그대로.** */
export async function atStage<T>(stage: PaymentWidgetStage, run: () => T | Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    if (err instanceof PaymentWidgetError) throw err;
    throw new PaymentWidgetError(stage, tossErrorCode(err));
  }
}

/** 붙인 위젯 하나. **화면이 쓰는 것은 이 셋뿐이다.** */
export interface PaymentWidgetHandle {
  /** 금액을 다시 올린다. ⛔ 화면이 계산한 값을 넣지 마라. 서버가 준 `amount` 그대로다. */
  setAmount(amount: number): Promise<void>;
  /** 결제창으로 보낸다. **돌아오는 길은 `successUrl`·`failUrl` 이다**(전체 이동이다). */
  requestPayment(input: RequestPaymentInput): Promise<void>;
  /** 붙여 둔 DOM 을 뗀다. 화면이 벗어날 때 부른다. */
  destroy(): Promise<void>;
}

export interface RequestPaymentInput {
  /** **서버가 만든 주문번호.** ⛔ 화면이 만들지 마라 — 승인 단계가 이 값에서 작업을
   *  되읽으므로, 화면이 만들면 그 대조가 「화면이 만든 값과 화면이 만든 값」을 비교한다. */
  orderId: string;
  /** 결제창에 뜨는 주문 이름. 셀러가 읽는 자리다. */
  orderName: string;
  successUrl: string;
  failUrl: string;
}

// ── SDK 타입. **우리가 쓰는 칸만 적는다** ───────────────────────────────────
//
// ⛔ `any` 로 덮지 않는다. 타입을 켜 둔 이유가 여기서 막히기 위해서인데, 돈이 움직이는
//    호출의 인자 모양을 컴파일러가 안 보면 오타가 런타임 결제 실패로만 드러난다.

interface TossRendered {
  destroy(): Promise<void>;
}

interface TossWidgets {
  setAmount(input: { currency: "KRW"; value: number }): Promise<void>;
  renderPaymentMethods(input: { selector: string; variantKey?: string }): Promise<TossRendered>;
  renderAgreement(input: { selector: string; variantKey?: string }): Promise<TossRendered>;
  requestPayment(input: RequestPaymentInput): Promise<void>;
}

interface TossPaymentsInstance {
  widgets(input: { customerKey: string }): TossWidgets;
}

type TossPaymentsFactory = ((clientKey: string) => TossPaymentsInstance) & {
  /** 저장된 결제 수단을 안 쓰는 손님. 아래 `mountPaymentWidget` 머리말 참고. */
  ANONYMOUS: string;
};

declare global {
  interface Window {
    TossPayments?: TossPaymentsFactory;
  }
}

/** 한 번만 싣는다. **같은 화면을 두 번 열어도 `<script>` 가 둘이 되지 않는다.** */
let pending: Promise<TossPaymentsFactory> | null = null;

export function loadTossPayments(): Promise<TossPaymentsFactory> {
  if (window.TossPayments) return Promise.resolve(window.TossPayments);
  if (pending) return pending;

  pending = new Promise<TossPaymentsFactory>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_SRC}"]`);
    const script = existing ?? document.createElement("script");
    let settled = false;

    const done = (err: WidgetLoadError | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (err) {
        // ⛔ **실패한 시도를 캐시에 남기지 않는다.** 남기면 셀러가 다시 눌러도 같은 실패가
        //    즉시 돌아오고, 연결이 돌아왔는데도 영영 안 열린다.
        pending = null;
        script.remove();
        reject(err);
        return;
      }
      const factory = window.TossPayments;
      if (!factory) {
        pending = null;
        reject(new WidgetLoadError("SDK_MISSING", "결제창 코드를 읽었지만 쓸 수 없다"));
        return;
      }
      resolve(factory);
    };

    const timer = window.setTimeout(
      () => done(new WidgetLoadError("SDK_TIMEOUT", "결제창을 제때 불러오지 못했다")),
      SDK_TIMEOUT_MS,
    );

    script.addEventListener("load", () => done(null));
    script.addEventListener("error", () => done(new WidgetLoadError("SDK_LOAD_FAILED", "결제창을 불러오지 못했다")));

    if (!existing) {
      script.src = SDK_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return pending;
}

export interface MountWidgetInput {
  /** **서버가 준 값이다.** 소스에 박힌 값이 여기 오면 안 된다(이 파일 머리말). */
  clientKey: string;
  /** 서버가 정한 금액. */
  amount: number;
  /** 결제 수단을 그릴 자리의 CSS 선택자. */
  methodsSelector: string;
  /** 약관 동의를 그릴 자리의 CSS 선택자. */
  agreementSelector: string;
}

/** 위젯을 붙이고 손잡이를 돌려준다.
 *
 * ## ⛔ `customerKey` 에 셀러를 식별하는 값을 넣지 않는다
 *
 * `ANONYMOUS` 를 쓴다. 저장된 카드를 다음에 다시 고르는 편의를 포기하는 대신, **셀러를
 * 가리키는 값이 PG 로 나가지 않는다.** 그 편의를 켜는 것은 「무엇을 보내기로 했는가」를
 * 정하는 일이라 법무·기획의 몫이고, 이 회차가 조용히 정할 것이 아니다.
 *
 * ⚠️ **약관 동의 위젯을 함께 그린다.** 빠뜨리면 결제창이 열리기는 하는데 승인 단계에서
 * 막힌다. 두 자리를 한 함수가 함께 세우는 이유가 그것이다. */
export async function mountPaymentWidget(input: MountWidgetInput): Promise<PaymentWidgetHandle> {
  // ⭐ **단계마다 따로 잡는다**(2026-10-17). 「유효하지 않은 키」가 `widgets()` 에서 나는지 그리기에서
  //    나는지가 진단의 갈림길인데, 한 `try` 로 묶으면 그 구분이 사라진다.
  const TossPayments = await atStage("load", () => loadTossPayments());
  const widgets = await atStage("widgets", () =>
    TossPayments(input.clientKey).widgets({ customerKey: TossPayments.ANONYMOUS }),
  );

  // **금액이 먼저다.** 토스는 금액이 정해지기 전에 그린 결제 수단을 받지 않는다.
  await atStage("amount", () => widgets.setAmount({ currency: "KRW", value: input.amount }));
  const rendered = await atStage("render", () =>
    Promise.all([
      widgets.renderPaymentMethods({ selector: input.methodsSelector, variantKey: "DEFAULT" }),
      widgets.renderAgreement({ selector: input.agreementSelector, variantKey: "AGREEMENT" }),
    ]),
  );

  return {
    setAmount: (amount) => atStage("amount", () => widgets.setAmount({ currency: "KRW", value: amount })),
    // 셀러가 결제창을 스스로 닫아도 여기로 온다(`USER_CANCEL` 등). 그 갈래를 가르는 것은 화면이다.
    requestPayment: (payment) => atStage("request", () => widgets.requestPayment(payment)),
    async destroy() {
      // 하나가 실패해도 나머지는 뗀다. 안 떼면 다음 진입에서 같은 자리에 두 번 그려진다.
      await Promise.allSettled(rendered.map((one) => one.destroy()));
    },
  };
}

// ── 결제창에서 돌아오는 길 ───────────────────────────────────────────────────
//
// ⭐ **결제창은 전체 이동이다.** `requestPayment` 는 값을 돌려주지 않고 브라우저를 토스로
//    보냈다가 우리 주소로 되돌린다. 그래서 「돌아온 요청을 읽는 일」이 필요하고, 그 읽기가
//    한 곳에 있어야 성공과 실패를 가르는 규칙이 화면마다 갈리지 않는다.

/** 결제창에서 돌아온 요청. */
export type WidgetReturn =
  | { kind: "NONE" }
  /** 승인할 것이 왔다. **이 셋을 그대로 서버에 넘긴다.** */
  | { kind: "SUCCESS"; paymentKey: string; orderId: string; amount: number }
  /** 결제창이 실패로 끝났다. ⛔ **토스가 준 `message` 는 담지 않는다** — 그 문장은 셀러가
   *  읽을 우리 문구가 아니고, 화면 문구는 코드로 고른다(`lib/api.ts` 머리말의 규약). */
  | { kind: "FAIL"; code: string };

/** 주소창의 질의 문자열을 갈래로 읽는다. **성공은 `paymentKey` 가 있고 실패는 `code` 가 있다.**
 *
 * ⛔ **`amount` 를 믿지 않는다.** 여기서 읽은 수는 **대조용으로 서버에 넘기는 값**일 뿐이고,
 * 서버는 그 작업의 장수와 단가로 금액을 다시 만들어 맞대 본다(`lib/checkout.ts`). 화면이
 * 이 값으로 무엇을 계산하면 그 대조가 뜻을 잃는다. */
export function readWidgetReturn(search: URLSearchParams): WidgetReturn {
  const paymentKey = search.get("paymentKey");
  const orderId = search.get("orderId");
  const amount = Number(search.get("amount"));
  if (paymentKey && orderId && Number.isFinite(amount)) {
    return { kind: "SUCCESS", paymentKey, orderId, amount };
  }
  const code = search.get("code");
  if (code) return { kind: "FAIL", code };
  return { kind: "NONE" };
}

/** 결제창이 되돌려 보낼 주소. **성공과 실패가 같은 화면으로 온다.**
 *
 * 화면 하나가 「결제 전·결제됨·실패」 셋을 다 그리므로(`W-26` 은 오른쪽에 상태 셋을 편다)
 * 주소를 갈라 둘 이유가 없다. 갈래는 위 `readWidgetReturn` 이 질의 문자열로 읽는다. */
export function widgetReturnUrl(jobId: string): string {
  return `${window.location.origin}/jobs/${jobId}/pay`;
}
